import { ChevronRight, HelpCircle, Plus, X } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'
import { addSlot, DAY_LABELS, DAY_ORDER, MAX_SLOTS } from './model'
import styles from './ChannelEditDialog.module.scss'

/**
 * "?" que abre a ajuda no hover e no foco — e no toque, que o Tooltip do Radix
 * ignora: o clique alterna o estado em vez de fechar.
 */
export function Help({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <Tooltip content={children} open={open} onOpenChange={setOpen}>
      <button
        type="button"
        className={styles.help}
        aria-label={`Ajuda: ${label}`}
        onPointerDown={(e) => {
          if (e.pointerType === 'touch') e.preventDefault()
        }}
        onClick={(e) => {
          e.preventDefault()
          setOpen((o) => !o)
        }}
      >
        <HelpCircle aria-hidden />
      </button>
    </Tooltip>
  )
}

export interface FieldProps {
  id?: string | undefined
  /** Para grupo de botões (dias): o rótulo vira o nome do grupo, não um ``<label>``. */
  groupId?: string | undefined
  label: string
  help?: ReactNode | undefined
  /** Slot à direita do rótulo: "padrão do canal principal", "personalizado"… */
  meta?: ReactNode | undefined
  /** Valor salvo antes da alteração — mostra o ponto e "antes: X". */
  before?: string | null | undefined
  hint?: ReactNode | undefined
  error?: string | undefined
  className?: string | undefined
  children: ReactNode
}

/** Campo com rótulo em caixa alta, ajuda, estado do valor e mensagem. */
export function Field({ id, groupId, label, help, meta, before, hint, error, className, children }: FieldProps) {
  const changed = before !== undefined && before !== null
  return (
    <div className={cn(styles.field, changed && styles.changed, className)}>
      <div className={styles.labelRow}>
        {changed ? <span className={styles.changedDot} aria-hidden /> : null}
        {id && !groupId ? (
          <label className={styles.label} htmlFor={id}>
            {label}
          </label>
        ) : (
          <span className={styles.label} id={groupId ? `${groupId}-label` : undefined}>
            {label}
          </span>
        )}
        {help ? <Help label={label.toLowerCase()}>{help}</Help> : null}
        {changed ? (
          <span className={cn(styles.meta, styles.metaChanged)}>antes: {before}</span>
        ) : meta ? (
          <span className={styles.meta}>{meta}</span>
        ) : null}
      </div>
      {children}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint}>{hint}</p>
      ) : null}
    </div>
  )
}

export function Section({
  title,
  meta,
  children,
  className,
  ...rest
}: {
  title?: ReactNode | undefined
  meta?: ReactNode | undefined
  children: ReactNode
  className?: string | undefined
  'aria-busy'?: boolean | undefined
  id?: string | undefined
}) {
  return (
    <section className={cn(styles.section, className)} {...rest}>
      {title ? (
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>{title}</h3>
          {meta ? <span className={styles.sectionMeta}>{meta}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  )
}

export interface NumberInputProps {
  id: string
  value: number | null
  onChange: (value: number | null) => void
  min: number
  max: number
  suffix?: string | undefined
  placeholder?: string | undefined
  error?: boolean | undefined
  'aria-label'?: string | undefined
  'aria-describedby'?: string | undefined
  /** Vazio vira ``null`` em vez de inválido (campo com "padrão do sistema"). */
  allowEmpty?: boolean | undefined
}

/** Número inteiro com sufixo ("0–10"). Vazio fica inválido, salvo ``allowEmpty``. */
export function NumberInput({
  id,
  value,
  onChange,
  min,
  max,
  suffix,
  placeholder,
  error,
  allowEmpty,
  ...aria
}: NumberInputProps) {
  return (
    <div className={styles.suffixInput}>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        {...(placeholder ? { placeholder } : {})}
        value={value === null || Number.isNaN(value) ? '' : String(value)}
        error={Boolean(error)}
        onChange={(e) => {
          const raw = e.target.value
          if (raw === '') {
            onChange(allowEmpty ? null : Number.NaN)
            return
          }
          onChange(Number(raw))
        }}
        {...aria}
      />
      {suffix ? <span className={styles.suffix}>{suffix}</span> : null}
    </div>
  )
}

/** Dias da semana (domingo primeiro), em dias ISO. */
export function DayPicker({
  value,
  onChange,
  id,
}: {
  value: readonly number[]
  onChange: (days: number[]) => void
  id?: string
}) {
  return (
    <div
      className={styles.dayPicker}
      role="group"
      id={id}
      {...(id ? { 'aria-labelledby': `${id}-label` } : { 'aria-label': 'Dias da semana' })}
    >
      {DAY_ORDER.map((day) => {
        const on = value.includes(day)
        return (
          <button
            key={day}
            type="button"
            className={styles.day}
            aria-pressed={on}
            onClick={() => {
              onChange(on ? value.filter((d) => d !== day) : [...value, day].sort((a, b) => a - b))
            }}
          >
            {DAY_LABELS[day]}
          </button>
        )
      })}
    </div>
  )
}

/** Seção recolhível com resumo à direita. */
export function Disclosure({
  title,
  meta,
  defaultOpen,
  children,
}: {
  title: string
  meta?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen))
  const bodyId = useId()
  // Abre sozinha quando aparece erro ou valor fora do padrão depois de montar.
  const shown = open || Boolean(defaultOpen)
  return (
    <div className={styles.disclosure}>
      <button
        type="button"
        className={styles.disclosureButton}
        aria-expanded={shown}
        aria-controls={bodyId}
        onClick={() => {
          setOpen(!shown)
        }}
      >
        <ChevronRight aria-hidden />
        {title}
        {meta ? <span className={styles.disclosureMeta}>{meta}</span> : null}
      </button>
      {shown ? (
        <div id={bodyId} className={styles.disclosureBody}>
          {children}
        </div>
      ) : null}
    </div>
  )
}

/** Chips de horário + campo para adicionar: ordena, recusa duplicado, até 10. */
export function TimeChipInput({
  value,
  onChange,
  id,
}: {
  value: readonly string[]
  onChange: (slots: string[]) => void
  id: string
}) {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  function add() {
    const next = addSlot(value, draft)
    setError(next.error)
    if (!next.error) {
      onChange(next.slots)
      setDraft('')
    }
  }

  return (
    <>
      <div className={styles.slotChips}>
        {value.length === 0 ? (
          <span className={styles.empty}>Sem slots — o upload não sairá neste canal.</span>
        ) : (
          value.map((slot) => (
            <span key={slot} className={styles.slotChip}>
              {slot}
              <button
                type="button"
                className={styles.slotRemove}
                aria-label={`Remover slot ${slot}`}
                onClick={() => {
                  onChange(value.filter((s) => s !== slot))
                }}
              >
                <X aria-hidden />
              </button>
            </span>
          ))
        )}
      </div>
      {value.length < MAX_SLOTS ? (
        <div className={styles.slotAdd}>
          <Input
            id={id}
            aria-label="Novo slot"
            placeholder="HH:MM"
            value={draft}
            error={Boolean(error)}
            onChange={(e) => {
              setDraft(e.target.value)
              setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                add()
              }
            }}
          />
          <IconButton type="button" aria-label="Adicionar slot" variant="outline" onClick={add}>
            <Plus />
          </IconButton>
        </div>
      ) : null}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </>
  )
}
